import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { VenueCode } from '#/products/types';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import {
  DrfqInstruments,
  GrfqInstruments,
  INITIAL_INSTRUMENTS_STATE,
  UnifiedInstruments,
} from './types';
import { useDrfqInstrumentsFetcher } from './drfq';
import { useGrfqInstrumentsFetcher } from './grfq';

type DrfqVenueInstruments = Record<VenueCode, DrfqInstruments>;
type GrfqVenueInstruments = Record<VenueCode, GrfqInstruments>;
type Status = 'idle' | 'loading';
interface InstrumentsByAuction {
  OB: GrfqVenueInstruments;
  RFQ: DrfqVenueInstruments;
}

interface InstrumentsFetcher {
  fetch: (
    auctionType: AuctionType,
    venue: VenueCode,
    cb?: (instruments: UnifiedInstruments) => void,
  ) => void;
  status: Status;
}

const VENUES_TO_PREFETCH = ['DBT'] as const;
const INITIAL_AUCTION_STATE = {
  DBT: INITIAL_INSTRUMENTS_STATE,
  BYB: INITIAL_INSTRUMENTS_STATE,
  BIT: INITIAL_INSTRUMENTS_STATE,
  PRDX: INITIAL_INSTRUMENTS_STATE,
  BLSH: INITIAL_INSTRUMENTS_STATE,
} as const;
const INITIAL_STATE = {
  OB: INITIAL_AUCTION_STATE,
  RFQ: INITIAL_AUCTION_STATE,
} as const;

const [useDrfqInstruments, DrfqInstrumentsProvider] =
  createSafeContext<DrfqVenueInstruments>('DrfqVenueInstruments');
const [useGrfqInstruments, GrfqInstrumentsProvider] =
  createSafeContext<GrfqVenueInstruments>('GrfqVenueInstruments');
const [useInstrumentsFetcher, InstrumentsFetcherProvider] =
  createSafeContext<InstrumentsFetcher>('InstrumentsFetcher');

export { useDrfqInstruments, useGrfqInstruments, useInstrumentsFetcher };

export default function InstrumentsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const drfqFetcher = useDrfqInstrumentsFetcher();
  const grfqFetcher = useGrfqInstrumentsFetcher();
  const [status, setStatus] = useState<Status>('idle');
  const [instrumentsByAuction, setInstrumentsByAuction] =
    useState<InstrumentsByAuction>(INITIAL_STATE);

  const fetchInstrumentsVenue: InstrumentsFetcher['fetch'] = useCallback(
    (auction, venue, cb) => {
      const fetcher = auction === 'OB' ? grfqFetcher : drfqFetcher;
      setStatus('loading');
      fetcher(venue, (instruments) => {
        setStatus('idle');
        cb?.(instruments);
        setInstrumentsByAuction((prev) => ({
          ...prev,
          [auction]: { ...prev[auction], [venue]: instruments },
        }));
      });
    },
    [drfqFetcher, grfqFetcher],
  );

  const fetchAll = useCallback(() => {
    VENUES_TO_PREFETCH.map((venue) => fetchInstrumentsVenue('RFQ', venue));
    VENUES_TO_PREFETCH.map((venue) => fetchInstrumentsVenue('OB', venue));
  }, [fetchInstrumentsVenue]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetcher = useMemo(
    () => ({ fetch: fetchInstrumentsVenue, status }),
    [fetchInstrumentsVenue, status],
  );

  return (
    <InstrumentsFetcherProvider value={fetcher}>
      <DrfqInstrumentsProvider value={instrumentsByAuction['RFQ']}>
        <GrfqInstrumentsProvider value={instrumentsByAuction['OB']}>
          {children}
        </GrfqInstrumentsProvider>
      </DrfqInstrumentsProvider>
    </InstrumentsFetcherProvider>
  );
}
